import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDemoMode, type DemoPersona } from '../context/DemoModeContext.js';
import type { DemoRole } from '@infoscend-claims/shared';
import { api } from '../lib/api.js';
import { homeRouteFor } from '../lib/roleRoutes.js';

interface TenancyUser {
  id: string;
  name: string;
  role: string;
  orgName?: string;
  distributorOrgId?: string;
}

interface Tenancy {
  brandPrincipal: { name: string };
  brandUsers: TenancyUser[];
  distributorUsers: (TenancyUser & { distributorOrgName: string })[];
}

function deriveRole(user: TenancyUser, isBrand: boolean): DemoRole {
  if (!isBrand) return 'distributor_submitter';
  if (user.role === 'ADMIN') return 'brand_admin';
  return 'brand_approver';
}

export function DemoRoleSwitcher() {
  const { t } = useTranslation();
  const { persona, setPersona } = useDemoMode();
  const navigate = useNavigate();
  const isDemoMode = import.meta.env['VITE_DEMO_MODE'] === 'true';
  const [personas, setPersonas] = useState<DemoPersona[]>([]);

  useEffect(() => {
    api.get<Tenancy>('/demo/tenancy')
      .then((data) => {
        const list: DemoPersona[] = [
          ...data.brandUsers.map((u) => ({
            role: deriveRole(u, true),
            userId: u.id,
            displayName: u.name,
            orgName: data.brandPrincipal.name,
          })),
          ...data.distributorUsers.map((u) => ({
            role: deriveRole(u, false),
            userId: u.id,
            displayName: u.name,
            orgName: u.distributorOrgName,
          })),
        ];
        setPersonas(list);
      })
      .catch(() => {
        // API not reachable — keep defaults
      });
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const found = personas.find((p) => p.userId === e.target.value);
    if (found) {
      setPersona(found);
      navigate(homeRouteFor(found.role));
    }
  };

  const personaLabel = (p: DemoPersona) =>
    `${t(`roles.${p.role}`)} — ${p.displayName} (${p.orgName})`;

  return (
    <div className="flex items-center gap-2">
      {isDemoMode && (
        <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-copper-deep bg-copper-soft px-2 py-0.5 rounded">
          {t('app.demoModeBadge')}
        </span>
      )}
      <div className="flex items-center gap-1.5">
        <span className="text-xs text-slate-500 font-medium hidden sm:inline">
          {t('roleSwitcher.label')}:
        </span>
        <select
          value={persona.userId}
          onChange={handleChange}
          disabled={personas.length === 0}
          className="bg-bone-soft border border-slate-200 focus:border-copper rounded px-2 py-1 text-xs text-ink font-medium outline-none cursor-pointer transition-colors duration-200 disabled:opacity-50 max-w-[260px]"
          aria-label={t('roleSwitcher.label')}
        >
          {personas.length === 0 ? (
            <option value={persona.userId}>{personaLabel(persona)}</option>
          ) : (
            personas.map((p) => (
              <option key={p.userId} value={p.userId}>
                {personaLabel(p)}
              </option>
            ))
          )}
        </select>
      </div>
    </div>
  );
}
